import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Minus, Plus, ShoppingBag, Heart, X } from "lucide-react";
import { Link } from "react-router-dom";
import { imgUrl, formatINR } from "../lib/api";
import { useStore } from "../context/StoreContext";
import { toast } from "sonner";

export function QuickView({ product, onClose }) {
  const { addToCart, toggleWishlist } = useStore();
  const [qty, setQty] = useState(1);
  const [img, setImg] = useState(0);
  const [variant, setVariant] = useState("");
  const [selections, setSelections] = useState({});

  useEffect(() => { setQty(1); setImg(0); setVariant(""); setSelections({}); }, [product?.id]);

  if (!product) return null;
  const hasVariants = product.variants?.length > 0;
  const groups = (product.variant_groups || []).map((g) => ({ ...g, options: (g.options || []).filter((o) => o.is_active) })).filter((g) => g.options.length);
  const missingGroups = groups.filter((g) => !selections[g.name]);
  const selectedOptions = groups.map((g) => g.options.find((o) => o.label === selections[g.name])).filter(Boolean);
  const variantPrice = selectedOptions.reduce((acc, o) => (o.price != null ? o.price : acc), product.price);

  const add = () => {
    if (groups.length && missingGroups.length) {
      toast.error(`Please choose a ${missingGroups[0].name} first`);
      return;
    }
    if (hasVariants && !variant) {
      toast.error("Please choose a design first");
      return;
    }
    const variantStr = groups.length ? groups.map((g) => selections[g.name]).join(" / ") : variant;
    addToCart(groups.length ? { ...product, price: variantPrice } : product, qty, variantStr);
    onClose();
  };

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl overflow-hidden rounded-3xl border-white/60 bg-white/90 p-0 backdrop-blur-xl" data-testid="quickview-modal">
        <div className="grid sm:grid-cols-2">
          <div className="relative aspect-square bg-secondary/40 sm:aspect-auto">
            {product.images?.[img] ? (
              <img src={imgUrl(product.images[img])} alt={product.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center font-display text-6xl text-primary/40">A</div>
            )}
            {product.images?.length > 1 && (
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                {product.images.map((_, i) => (
                  <button key={i} data-testid={`quickview-thumb-${i}`} onClick={() => setImg(i)}
                    className={`h-1.5 rounded-full transition-all ${i === img ? "w-5 bg-primary" : "w-1.5 bg-foreground/25"}`} aria-label={`Image ${i + 1}`} />
                ))}
              </div>
            )}
          </div>
          <div className="flex flex-col p-6 sm:p-8">
            <h3 className="font-display text-xl leading-snug sm:text-2xl">{product.name}</h3>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-lg font-medium text-primary" data-testid="quickview-price">{variantPrice > 0 ? formatINR(variantPrice) : "Price on request"}</span>
              {product.price > 0 && product.compare_at_price > product.price && (
                <span className="text-sm text-muted-foreground line-through">{formatINR(product.compare_at_price)}</span>
              )}
            </div>
            <p className="mt-4 line-clamp-4 text-sm font-light leading-relaxed text-muted-foreground">{product.description}</p>
            {groups.map((g) => (
              <div className="mt-4" key={g.name} data-testid={`quickview-group-${g.name}`}>
                <p className="text-[10px] tracking-[0.25em] text-muted-foreground uppercase">Choose {g.name}</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {g.options.map((o) => {
                    const outOfStock = o.stock != null && o.stock <= 0;
                    return (
                      <button key={o.label} data-testid={`qv-variant-${g.name}-${o.label}`} disabled={outOfStock}
                        onClick={() => setSelections((s) => ({ ...s, [g.name]: o.label }))}
                        className={`rounded-full border px-3 py-2 text-xs font-medium transition-all ${selections[g.name] === o.label ? "border-primary bg-primary text-white shadow-md" : "border-input bg-white/70 hover:border-primary/50"} ${outOfStock ? "cursor-not-allowed opacity-40 line-through" : ""}`}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            {hasVariants && (
              <div className="mt-4">
                <p className="text-[10px] tracking-[0.25em] text-muted-foreground uppercase">Choose your ring design</p>
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="quickview-variants">
                  {product.variants.map((v) => (
                    <button key={v} data-testid={`qv-variant-${v}`} onClick={() => setVariant(v)}
                      className={`h-9 w-9 rounded-full border text-xs font-medium transition-all ${variant === v ? "border-primary bg-primary text-white shadow-md" : "border-input bg-white/70 hover:border-primary/50"}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-auto pt-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center rounded-full border border-input">
                  <button data-testid="quickview-qty-minus" onClick={() => setQty(Math.max(1, qty - 1))} className="p-2.5" aria-label="Decrease"><Minus size={14} /></button>
                  <span className="w-8 text-center text-sm" data-testid="quickview-qty">{qty}</span>
                  <button data-testid="quickview-qty-plus" onClick={() => setQty(Math.min(20, qty + 1))} className="p-2.5" aria-label="Increase"><Plus size={14} /></button>
                </div>
                <button data-testid="quickview-wishlist" onClick={() => toggleWishlist(product)} className="rounded-full border border-input p-2.5 hover:bg-secondary" aria-label="Wishlist">
                  <Heart size={16} />
                </button>
              </div>
              <button data-testid="quickview-add-to-cart" onClick={add}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-primary py-3 text-xs font-medium tracking-widest text-primary-foreground uppercase transition-colors hover:bg-foreground">
                <ShoppingBag size={15} /> Add to bag
              </button>
              <Link to={`/product/${product.id}`} onClick={onClose} data-testid="quickview-full-details"
                className="mt-3 block text-center text-xs tracking-widest text-muted-foreground uppercase underline-offset-4 hover:underline">
                View full details
              </Link>
            </div>
          </div>
        </div>
        <button onClick={onClose} data-testid="quickview-close" className="absolute right-4 top-4 rounded-full bg-white/80 p-1.5 shadow" aria-label="Close">
          <X size={16} />
        </button>
      </DialogContent>
    </Dialog>
  );
}
